import React, { FC, memo, useMemo, useState } from 'react';
import { Segment, Row, Col, StatusTip, Icon, Bubble } from 'tea-component';
import { RevenueUnitsSegment } from '../../_config/constant';
import MiddleEchart from './DigitalBarChart';
import DigitalLineChart from './DigitalLineChart';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import './index.scss';
import TipContext from '@/pages/home/components/TipContext';
import { useIntl, getLocale } from 'umi';

const { LoadingTip } = StatusTip;

interface RevenueUnitsTrendsProps {
  loading: boolean;
  trendsData: any;
}

const RevenueUnitsTrends: FC<RevenueUnitsTrendsProps> = (props) => {
  const { loading, trendsData } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [activeSegment, setActiveSegment] = useState<'revenue' | 'unit'>(
    'revenue',
  );

  const segmentList = useMemo(()=> {
    return RevenueUnitsSegment?.map(item=> ({
      ...item,
      text: formatMessage({id: item?.text || ''})
    }))
  },[locale, RevenueUnitsSegment])

  const [show, setShow] = useState(false);

  const trendsDataList = useMemo(() => {
    return (
      trendsData?.[activeSegment]?.map((item, index) => ({
        ...item,
        date: `${formatMessage({id: 'dbi_benchmark_year'}, { num: index + 1})}`,
        metrics: item?.metrics?.map((config) => ({
          metric: formatMessage({id: `dbi_${config?.type?.toLowerCase()}` || ''}),
          total: config?.value,
        })),
      })) || []
    );
  }, [trendsData?.[activeSegment], activeSegment, locale]);

  const proportionData = useMemo(() => {
    const data = trendsData?.[activeSegment]?.reduce(
      (result, item, index) => {
        result?.linexAxis?.push(`${formatMessage({id: 'dbi_benchmark_year'}, { num: index + 1})}`);
        const pcMetrics = item?.metrics?.find(
          (config) => config?.type === 'pc',
        );
        const consoleMetrics = item?.metrics?.find(
          (config) => config?.type === 'console',
        );
        result?.pcLineData?.push(((pcMetrics?.dod || 0) * 100).toFixed(2) || 0);
        result?.consoleLineData?.push(
          ((consoleMetrics?.dod || 0) * 100).toFixed(2) || 0,
        );
        return result;
      },
      { linexAxis: [], pcLineData: [], consoleLineData: [] } as any,
    );

    return {
      linexAxis: data?.linexAxis || [],
      lineSeries: [
        {
          type: 'line',
          showSymbol: false,
          symbolSize: 10,
          symbol: 'circle',
          emphasis: {
            itemStyle: {
              borderWidth: 1,
              borderColor: '#fff'
            }
          },
          name: formatMessage({id: activeSegment === 'revenue'? 'dbi_benchmark_pc_revenue_pro': 'dbi_benchmark_pc_units_pro'}),
          data: data?.pcLineData || [],
        },
        {
          type: 'line',
          showSymbol: false,
          symbolSize: 10,
          symbol: 'circle',
          emphasis: {
            itemStyle: {
              borderWidth: 1,
              borderColor: '#fff'
            }
          },
          name: formatMessage({id: activeSegment === 'revenue'? 'dbi_benchmark_console_revenue_pro': 'dbi_benchmark_console_units_pro'}),
          data: data?.consoleLineData || [],
        },
      ],
    };
  }, [trendsData?.[activeSegment], activeSegment, locale]);
  return (
    <CardSlotV2
      className="dbi-benchmark-cardbox dbi-benchmark-revenue-units-trends"
      iconList={[]}
      title={
        <>
          <div>{formatMessage({id: 'dbi_benchmark_us_digital_revenue_units'})}</div>
          <TipContext
            className="tip-icon"
            AWidth={555}
            placement="top"
            tipText={
              formatMessage(
                {id: activeSegment === 'revenue' ? 'dbi_benchmark_us_digital_revenue_info' : 
                'dbi_benchmark_us_digital_unit_info'}
              )
            }
          />
        </>
      }
    >
      <Segment
        options={segmentList}
        className="revenue-units-segment"
        value={activeSegment}
        onChange={(value) => {
          setShow(true);
          setActiveSegment(value);
          setTimeout(() => {
            setShow(false);
          }, 150);
        }}
      />
      <Row>
        {loading || show ? (
          <LoadingTip className="dbi-benchmark-chart-loading" />
        ) : (
          <>
            <Col style={{ marginRight: 10 }}>
              <div className="revenue-chart-title">
                 {formatMessage({id: activeSegment === 'revenue' ? 'dbi_revenue_$' : 'dbi_metric_units'})}
                <TipContext
                  className="tip-icon"
                  AWidth={555}
                  placement="top"
                  tipText={
                    formatMessage(({
                      id: activeSegment === 'revenue' ? 'dbi_benchmark_digital_revenue_info' :
                       'dbi_benchmark_digital_units_info'
                    }))
                  }
                />
              </div>
              {trendsDataList?.length > 0 ? (
                <MiddleEchart
                  data={trendsDataList}
                  height={360}
                  // legendRender={regionLegendRender}
                  defaultMinPoin={0}
                />
              ) : (
                <div className="revenue-nodata-container">
                  {formatMessage({id: 'dbi_detail_no_data_under'})}
                </div>
              )}
            </Col>
            <Col style={{ marginLeft: 10 }}>
              <div className="revenue-chart-title">
                {formatMessage({id: activeSegment === 'revenue' ? 'dbi_benchmark_revenue_p' : 'dbi_benchmark_units_p'})}
                <TipContext
                  className="tip-icon"
                  AWidth={555}
                  placement="top"
                  tipText={formatMessage({id: 'dbi_benchmark_unit_info'})}
                />
              </div>
              {proportionData?.lineSeries?.[0]?.data?.length > 0 ? (
                <DigitalLineChart
                  lineSeries={proportionData?.lineSeries || []}
                  lineXAxis={proportionData?.linexAxis || []}
                />
              ) : (
                <div className="revenue-nodata-container">
                  {formatMessage({id: 'dbi_detail_no_data_under'})}
                </div>
              )}
            </Col>
          </>
        )}
      </Row>
    </CardSlotV2>
  );
};

RevenueUnitsTrends.displayName = 'RevenueUnitsTrends';

export default RevenueUnitsTrends;
